/* eslint-disable react/prop-types */
"use client";

import React from "react";
import { AnimatePresence, motion } from "framer-motion";

const SkeletonStyles = () => (
  <style>
    {`
      @keyframes vgEventToolsSkeleton {
        0% { transform: translateX(-120%); }
        100% { transform: translateX(120%); }
      }
    `}
  </style>
);

export const EventToolsSkeletonBlock = ({ className = "", ...rest }) => (
  <div
    className={`relative overflow-hidden rounded-[18px] bg-white/[0.075] shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] ${className}`}
    {...rest}
  >
    <div
      className="absolute inset-y-0 left-0 w-[58%] bg-gradient-to-r from-transparent via-white/16 to-transparent"
      style={{ animation: "vgEventToolsSkeleton 1.35s ease-in-out infinite" }}
      aria-hidden="true"
    />
  </div>
);

const SkeletonLineStack = ({ lines = 3 }) => (
  <div className="space-y-2.5">
    {Array.from({ length: lines }).map((_, index) => (
      <EventToolsSkeletonBlock
        key={index}
        className={`h-4 ${index === lines - 1 ? "mx-auto w-[58%]" : "w-full"}`}
      />
    ))}
  </div>
);

const SkeletonFooter = ({ two = false }) => (
  <div className={`mt-5 grid gap-3 ${two ? "grid-cols-2" : "grid-cols-1"}`}>
    <EventToolsSkeletonBlock className="h-12 rounded-full" />
    {two ? <EventToolsSkeletonBlock className="h-12 rounded-full" /> : null}
  </div>
);

const WaitlistSkeleton = () => (
  <>
    <SkeletonLineStack lines={3} />
    <div className="mt-6 flex gap-3 overflow-hidden px-1">
      {Array.from({ length: 7 }).map((_, index) => (
        <div key={index} className="flex shrink-0 flex-col items-center gap-2">
          <EventToolsSkeletonBlock className="h-4 w-8 rounded-full" />
          <EventToolsSkeletonBlock className="h-[55px] w-[55px] rounded-full" />
          <EventToolsSkeletonBlock className="h-4 w-12 rounded-full" />
        </div>
      ))}
    </div>
    <SkeletonFooter />
  </>
);

const ParticipantsSkeleton = () => (
  <>
    <SkeletonLineStack lines={2} />
    <EventToolsSkeletonBlock className="mt-5 h-11 rounded-full" />
    <div className="mt-4 grid grid-cols-4 gap-2.5 sm:grid-cols-6">
      {Array.from({ length: 12 }).map((_, index) => (
        <div key={index} className="flex min-h-[92px] flex-col items-center justify-center gap-2 rounded-[16px]">
          <EventToolsSkeletonBlock className="h-[52px] w-[52px] rounded-full" />
          <EventToolsSkeletonBlock className="h-4 w-12 rounded-full" />
        </div>
      ))}
    </div>
    <EventToolsSkeletonBlock className="mx-auto mt-4 h-4 w-[55%] rounded-full" />
    <SkeletonFooter />
  </>
);

const RoleSkeleton = () => (
  <>
    <SkeletonLineStack lines={2} />
    {Array.from({ length: 3 }).map((_, sectionIndex) => (
      <section key={sectionIndex} className="mt-5">
        <EventToolsSkeletonBlock className="h-4 w-[38%] rounded-full" />
        <div className="mt-3 flex gap-3 overflow-hidden">
          {Array.from({ length: sectionIndex === 0 ? 4 : 5 }).map((__, itemIndex) => (
            <div key={itemIndex} className="flex shrink-0 flex-col items-center gap-2">
              <EventToolsSkeletonBlock className="h-[56px] w-[56px] rounded-full" />
              <EventToolsSkeletonBlock className="h-4 w-12 rounded-full" />
              {sectionIndex > 0 ? (
                <EventToolsSkeletonBlock className="h-3 w-16 rounded-full" />
              ) : null}
            </div>
          ))}
        </div>
      </section>
    ))}
    <SkeletonFooter two />
  </>
);

const IntentionSkeleton = () => (
  <>
    <div className="space-y-3">
      <SkeletonLineStack lines={4} />
      <div className="rounded-[18px] border border-white/10 bg-white/[0.04] p-4">
        <EventToolsSkeletonBlock className="h-4 w-[42%] rounded-full" />
        <EventToolsSkeletonBlock className="mt-3 h-4 w-[72%] rounded-full" />
        <EventToolsSkeletonBlock className="mt-5 h-4 w-[44%] rounded-full" />
        <EventToolsSkeletonBlock className="mt-3 h-4 w-[64%] rounded-full" />
      </div>
      <EventToolsSkeletonBlock className="h-4 w-[78%] rounded-full" />
    </div>
    <SkeletonFooter two />
  </>
);

const AnalyticsSkeleton = () => (
  <>
    <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
      {Array.from({ length: 4 }).map((_, index) => (
        <div key={index} className="rounded-xl border border-white/12 bg-white/[0.04] px-3 py-2.5">
          <EventToolsSkeletonBlock className="h-3 w-[72%] rounded-full" />
          <EventToolsSkeletonBlock className="mt-3 h-7 w-[52%] rounded-full" />
        </div>
      ))}
    </div>
    <div className="mt-4 space-y-3">
      {Array.from({ length: 2 }).map((_, chartIndex) => (
        <div key={chartIndex} className="rounded-2xl border border-white/12 bg-white/[0.04] p-3">
          <EventToolsSkeletonBlock className="h-4 w-[32%] rounded-full" />
          <div className="mt-4 flex h-[180px] items-end gap-2 overflow-hidden">
            {Array.from({ length: 14 }).map((__, index) => (
              <EventToolsSkeletonBlock
                key={index}
                className="w-8 shrink-0 rounded-md"
                style={{ height: `${70 + ((index * 17) % 90)}px` }}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  </>
);

const DatePollSkeleton = () => (
  <>
    <SkeletonLineStack lines={2} />
    <EventToolsSkeletonBlock className="mt-5 h-12 rounded-[18px]" />
    <div className="mt-4 space-y-3">
      {Array.from({ length: 2 }).map((_, index) => (
        <div key={index} className="rounded-[18px] border border-white/10 bg-white/[0.04] p-3">
          <EventToolsSkeletonBlock className="h-4 w-[32%] rounded-full" />
          <div className="mt-3 grid grid-cols-3 gap-2">
            <EventToolsSkeletonBlock className="h-10 rounded-full" />
            <EventToolsSkeletonBlock className="h-10 rounded-full" />
            <EventToolsSkeletonBlock className="h-10 rounded-full" />
          </div>
          <div className="mt-3 grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_40px] gap-2">
            <EventToolsSkeletonBlock className="h-10 rounded-full" />
            <EventToolsSkeletonBlock className="h-10 rounded-full" />
            <EventToolsSkeletonBlock className="h-10 w-10 rounded-full" />
          </div>
        </div>
      ))}
    </div>
    <SkeletonFooter two />
  </>
);

const RequestsSkeleton = () => (
  <>
    <div className="space-y-4">
      {Array.from({ length: 2 }).map((_, index) => (
        <div key={index} className="rounded-[18px] border border-white/10 bg-white/[0.04] p-3">
          <div className="flex items-center gap-3">
            <EventToolsSkeletonBlock className="h-[56px] w-[56px] rounded-full" />
            <div className="min-w-0 flex-1 space-y-2">
              <EventToolsSkeletonBlock className="h-4 w-[36%] rounded-full" />
              <EventToolsSkeletonBlock className="h-3 w-[64%] rounded-full" />
            </div>
          </div>
          <div className="mt-4 space-y-2">
            <EventToolsSkeletonBlock className="h-4 w-full rounded-full" />
            <EventToolsSkeletonBlock className="h-4 w-[78%] rounded-full" />
          </div>
          <EventToolsSkeletonBlock className="mt-4 h-11 rounded-full" />
        </div>
      ))}
    </div>
    <SkeletonFooter />
  </>
);

const BoostSkeleton = () => (
  <>
    <SkeletonLineStack lines={2} />
    <div className="mt-5 rounded-2xl border border-white/10 bg-black/15 p-2">
      <EventToolsSkeletonBlock className="h-[316px] rounded-xl" />
    </div>
    <EventToolsSkeletonBlock className="mx-auto mt-4 h-12 w-[230px] rounded-2xl" />
  </>
);

const DeleteSkeleton = () => (
  <>
    <SkeletonLineStack lines={3} />
    <SkeletonFooter two />
  </>
);

const DEFAULT_VARIANTS = {
  waitlist: <WaitlistSkeleton />,
  participants: <ParticipantsSkeleton />,
  role: <RoleSkeleton />,
  intention: <IntentionSkeleton />,
  analytics: <AnalyticsSkeleton />,
  datePoll: <DatePollSkeleton />,
  requests: <RequestsSkeleton />,
  boost: <BoostSkeleton />,
  delete: <DeleteSkeleton />,
};

const EventToolsSkeleton = ({ variant = "default", className = "" }) => (
  <div className={className} aria-hidden="true">
    <SkeletonStyles />
    {DEFAULT_VARIANTS[variant] || <IntentionSkeleton />}
  </div>
);

export const EventToolsAnimatedState = ({
  loading = false,
  skeleton,
  children,
  className = "",
}) => (
  <AnimatePresence mode="wait" initial={false}>
    <motion.div
      key={loading ? "event-tools-skeleton" : "event-tools-content"}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {loading ? skeleton : children}
    </motion.div>
  </AnimatePresence>
);

export default EventToolsSkeleton;
